// Quest Service API for communicating with Supabase Edge Functions
import { supabase } from '@/lib/supabase';

export interface Quest {
  id: string; // Changed from number to string for UUID consistency
  displayId?: number;
  name: string;
  description: string;
  location: string;
  latitude?: number;
  longitude?: number;
  questType: string;
  difficulty: string;
  points: number;
  instructions: string;
  completionCriteria: string;
  riddle: string;
  riddleAnswer: string;
  photoTitle: string;
  photoDescription: string;
  educationalFacts: string[];
}

export interface Achievement {
  id: string; // Changed from number to string for UUID consistency
  title: string;
  description: string;
  icon: string;
  points: number;
  category: string;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  earned_date: string;
  achievement?: Achievement;
}

export interface ProximityResult {
  quest_id: string;
  quest_display_id: number;
  quest_title: string;
  distance: number;
  in_proximity: boolean;
  is_nearest: boolean;
  threshold: number;
  nearest_threshold: number;
  achievement_awarded?: {
    id: string;
    title: string;
  };
}

export interface LocationProximityResponse {
  user_location: {
    latitude: number;
    longitude: number;
  };
  proximity_results: ProximityResult[];
  message: string;
}

export interface Progress {
  id: string; // Changed from number to string for UUID consistency
  questId: string; // Changed from number to string for UUID consistency
  userId: string;
  status: string;
  completionPercentage: number;
  pointsEarned: number;
  riddleCompleted: boolean;
  photoSubmitted: boolean;
  completionDate?: string;
}

export interface RiddleValidationResult {
  correct: boolean;
  message: string;
  correctAnswer?: string;
}

class QuestService {

  // Quest endpoints
  async getQuestDetails(questId: string): Promise<Quest> {
    try {
      // The Edge Function expects questId in the URL path, not body
      const { data, error } = await supabase.functions.invoke(`get-quest-by-id/${questId}`);
      
      if (!error && data) {
        return data;
      }
      
      console.warn('Edge function failed, falling back to direct database query');
    } catch (error: any) {
      console.warn('Edge function error, falling back to direct database query:', error);
    }
    
    // Fallback: Query database directly
    return this.getQuestFromDatabase(questId);
  }

  private async getQuestFromDatabase(questId: string): Promise<Quest> {
    // Check if questId is a UUID or display_id
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    
    let query;
    if (uuidRegex.test(questId)) {
      // Query by UUID
      query = supabase
        .from('quests')
        .select('*')
        .eq('id', questId)
        .eq('active', true);
    } else {
      // Query by display_id
      const displayId = parseInt(questId, 10);
      if (isNaN(displayId)) {
        throw new Error('Invalid quest identifier');
      }
      query = supabase
        .from('quests')
        .select('*')
        .eq('display_id', displayId)
        .eq('active', true);
    }

    const { data, error } = await query.single();
    
    if (error || !data) {
      throw new Error(`Quest not found: ${questId}`);
    }

    // Convert database quest to Quest interface
    return {
      id: data.id,
      displayId: data.display_id,
      name: data.title,
      description: data.description || '',
      location: data.location_name || '',
      latitude: data.latitude,
      longitude: data.longitude,
      questType: data.quest_type,
      difficulty: data.difficulty,
      points: data.points,
      instructions: data.instructions || '',
      completionCriteria: data.completion_criteria || '',
      riddle: data.riddle || '',
      riddleAnswer: data.riddle_answer || '',
      photoTitle: data.photo_title || '',
      photoDescription: data.photo_description || '',
      educationalFacts: data.educational_facts || []
    };
  }

  async getAllQuests(): Promise<Quest[]> {
    // Get current session to determine if we should try edge function
    const { data: { session } } = await supabase.auth.getSession();
    const hasAuth = !!session?.access_token;
    
    if (hasAuth) {
      try {
        // Use Supabase client instead of direct fetch
        const { data, error } = await supabase.functions.invoke('get-all-quests');
        
        if (!error && data) {
          return data;
        }
        
        console.warn('QUEST SERVICE: Edge function failed, falling back to direct database query');
      } catch (error: any) {
        console.warn('QUEST SERVICE: Edge function error, falling back to direct database query:', error);
      }
    }
    
    // Fallback: Query database directly (works for both auth and anonymous users)
    return await this.getAllQuestsFromDatabase();
  }

  private async getAllQuestsFromDatabase(): Promise<Quest[]> {
    const { data, error } = await supabase
      .from('quests')
      .select('*')
      .eq('active', true)
      .order('order_index');

    if (error) {
      console.error('DATABASE: Quest fetch error:', error);
      throw new Error(`Failed to fetch quests: ${error.message}`);
    }

    if (!data || data.length === 0) {
      console.warn('DATABASE: No quest records found in database!');
      return [];
    }

    const mappedQuests = (data || []).map((quest, index) => {

      // If quest ID is null or undefined, use display_id as a fallback
      const questId = quest.id || quest.display_id?.toString() || 'unknown';
      
      // Ensure we have coordinates for the Asiatic Lion quest
      let latitude = quest.latitude;
      let longitude = quest.longitude;
      
      // Hardcode coordinates for the Asiatic Lion quest if missing
      if (quest.title?.includes('Asiatic Lion') || quest.title?.includes('Roar of the Past')) {
        latitude = latitude || 31.748306;
        longitude = longitude || 35.175722;
      }
     
      // Hardcode coordinates for the Giraffe quest if missing - exact החצר האפריקאית location
      if (quest.title?.includes('Giraffe') || quest.title?.includes('Towering Heights')) {
        latitude = latitude || 31.744550;
        longitude = longitude || 35.170197;
      }
      
      // Hardcode coordinates for the Elephant quest if missing - exact "פילים" location
      if (quest.title?.includes('Elephant') || quest.title?.includes('Giants of Memory')) {
        latitude = latitude || 31.748293;
        longitude = longitude || 35.175732;
      }
      
      // Hardcode coordinates for the Lemur quest if missing - exact "למורים" location
      if (quest.title?.includes('Lemur') || quest.title?.includes('Ring-Tailed Acrobats')) {
        latitude = latitude || 31.747926;
        longitude = longitude || 35.176232;
      }
      
      return {
        id: questId,
        displayId: quest.display_id,
        name: quest.title,
        description: quest.description || '',
        location: quest.location_name || '',
        latitude: latitude,
        longitude: longitude,
        questType: quest.quest_type,
        difficulty: quest.difficulty,
        points: quest.points,
        instructions: quest.instructions || '',
        completionCriteria: quest.completion_criteria || '',
        riddle: quest.riddle || '',
        riddleAnswer: quest.riddle_answer || '',
        photoTitle: quest.photo_title || '',
        photoDescription: quest.photo_description || '',
        educationalFacts: quest.educational_facts || []
      };
    });
    
    // Verify all animal quests specifically
    const asiaticQuest = mappedQuests.find(q => q.name.includes('Asiatic Lion') || q.name.includes('Roar of the Past'));
    const giraffeQuest = mappedQuests.find(q => q.name.includes('Giraffe') || q.name.includes('Towering Heights'));
    const elephantQuest = mappedQuests.find(q => q.name.includes('Elephant') || q.name.includes('Giants of Memory'));
    const lemurQuest = mappedQuests.find(q => q.name.includes('Lemur') || q.name.includes('Ring-Tailed Acrobats'));
    
    if (!asiaticQuest) {
      console.warn('VERIFICATION: Asiatic Lion quest NOT found in mapped results!');
    }
    
    if (!giraffeQuest) {
      console.warn('VERIFICATION: Giraffe quest NOT found in mapped results!');
    }
    
    if (!elephantQuest) {
      console.warn('VERIFICATION: Elephant quest NOT found in mapped results!');
    }
    
    if (!lemurQuest) {
      console.warn('VERIFICATION: Lemur quest NOT found in mapped results!');
    }
    
    return mappedQuests;
  }

  async validateRiddle(questId: string, answer: string): Promise<RiddleValidationResult> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('validate-riddle', {
        body: { questId, answer }
      });
      
      if (error) {
        throw new Error(`Failed to validate riddle: ${error.message}`);
      }
      return data;
    } catch (error) {
      console.error('RIDDLE VALIDATION: Error:', error);
      throw error;
    }
  }

  // Progress endpoints
  async getUserProgress(questId?: string): Promise<Progress[]> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('get-user-progress', {
        body: questId ? { quest_id: questId } : {}
      });
      
      if (error) {
        throw new Error(`Failed to fetch user progress: ${error.message}`);
      }
      
      return data || [];
    } catch (error) {
      console.error('USER PROGRESS: Error loading progress:', error);
      
      // Return empty array on timeout to prevent blocking
      if (error instanceof Error && error.message.includes('timeout')) {
        console.warn('USER PROGRESS: Request timed out, returning empty array');
        return [];
      }
      
      throw error;
    }
  }

  async createProgress(questId: string): Promise<Progress> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('create-progress', {
        body: { quest_id: questId }
      });
      
      if (error) {
        throw new Error(`Failed to create progress: ${error.message}`);
      }
      
      return data;
    } catch (error) {
      console.error('CREATE PROGRESS: Error creating progress:', error);
      throw error;
    }
  }

  async updateRiddleCompletion(progressId: string): Promise<Progress> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke(`update-riddle-completion/${progressId}`, {
        method: 'POST'
      });
      
      if (error) {
        throw new Error(`Failed to update riddle completion: ${error.message}`);
      }
      
      return data;
    } catch (error) {
      console.error('RIDDLE COMPLETION: Error updating riddle completion:', error);
      throw error;
    }
  }

  async updatePhotoSubmission(progressId: string, photoFile: File): Promise<Progress> {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('progressId', progressId);
      formData.append('photo', photoFile);

      // Get auth session for Authorization header
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Use fetch for multipart/form-data upload
      const response = await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/update-photo-submission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update photo submission');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('PHOTO SUBMISSION: Error updating photo submission:', error);
      throw error;
    }
  }

  // Achievement endpoints
  async getUserAchievements(): Promise<UserAchievement[]> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('get-user-achievements');
      
      if (!error && data) {
        return data;
      }
      
      console.warn('USER ACHIEVEMENTS: Edge function failed, falling back to direct database query.');
    } catch (error) {
      console.error('USER ACHIEVEMENTS: Error loading achievements from Edge Function, falling back to direct database query:', error);
    }

    // Fallback: Query database directly
    return this.getUserAchievementsFromDatabase();
  }

  private async getUserAchievementsFromDatabase(): Promise<UserAchievement[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('DATABASE: Cannot get user achievements without a user.');
      return [];
    }

    const { data, error } = await supabase
      .from('user_achievements')
      .select(`
        *,
        achievement:achievements(*)
      `)
      .eq('user_id', user.id);

    if (error) {
      console.error('DATABASE: Error fetching user achievements from database:', error);
      throw error;
    }

    return data || [];
  }

  async getAllAchievements(): Promise<Achievement[]> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('get-all-achievements');
      
      if (error) {
        throw new Error(`Failed to fetch achievements: ${error.message}`);
      }
      
      return data || [];
    } catch (error) {
      console.error('ALL ACHIEVEMENTS: Error loading achievements:', error);
      
      // Return empty array on timeout to prevent blocking
      if (error instanceof Error && error.message.includes('timeout')) {
        console.warn('ALL ACHIEVEMENTS: Request timed out, returning empty array');
        return [];
      }
      
      throw error;
    }
  }

  async awardAchievement(achievementId: string): Promise<any> {
    try {
      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('award-achievement', {
        body: { achievement_id: achievementId }
      });
      
      if (error) {
        throw new Error(`Failed to award achievement: ${error.message}`);
      }
      
      return data;
    } catch (error) {
      console.error('AWARD ACHIEVEMENT: Error awarding achievement:', error);
      throw error;
    }
  }

  // Location proximity checking
  async checkLocationProximity(
    latitude: number, 
    longitude: number, 
    questId?: string
  ): Promise<LocationProximityResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('check-location-proximity', {
        body: { 
          latitude, 
          longitude,
          ...(questId && { quest_id: questId })
        }
      });
      
      if (error) {
        throw new Error(`Failed to check location proximity: ${error.message}`);
      }
      
      return data;
    } catch (error) {
      console.error('LOCATION PROXIMITY: Error checking location proximity:', error);
      throw error;
    }
  }
}

export const questService = new QuestService();

// Utility function to truncate quest description to first paragraph with ellipsis
export function truncateQuestDescription(description: string, maxLength: number = 120): string {
  if (!description) return '';
  
  // First, try to find the first paragraph break (double newline or explicit paragraph separator)
  const paragraphBreak = description.indexOf('\n\n');
  if (paragraphBreak !== -1 && paragraphBreak < maxLength) {
    return description.substring(0, paragraphBreak).trim() + '...';
  }
  
  // If no paragraph break found or it's too long, truncate by length and find a good breaking point
  if (description.length <= maxLength) {
    return description;
  }
  
  // Truncate to maxLength and try to break at a sentence or word boundary
  const truncated = description.substring(0, maxLength);
  
  // Try to break at the end of a sentence
  const lastSentence = Math.max(
    truncated.lastIndexOf('.'),
    truncated.lastIndexOf('!'),
    truncated.lastIndexOf('?')
  );
  
  if (lastSentence > maxLength * 0.6) { // At least 60% of maxLength
    return truncated.substring(0, lastSentence + 1).trim();
  }
  
  // Fall back to breaking at a word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.6) {
    return truncated.substring(0, lastSpace).trim() + '...';
  }
  
  // Last resort: hard truncate
  return truncated.trim() + '...';
}